import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message } from "antd";
import styled from "styled-components";
import { CreatorDisplayLayout } from "../common/CreatorDisplayLayout";
import { creatorListAPI, CreatorWithAssignmentResponse } from "@/api/creatorList";
import { creatorAPI, CreatorCategory, CreatorTier } from "@/api/creator";
import { createShortUrl, buildShortUrl } from "@/api/shortUrl";
import { hasRole, ROLES } from "@/api/user";
import { extractAvatarUrl } from "@/shared/utils/socialProfile";
import { getFinalRateCurrencies } from "@/shared/utils/displaySettings";
import type { CreatorInterestStatus } from "@/shared/types/campaign";
import type { Creator } from "@/shared/types/campaign/creator";
import { LoadingWrapper } from "@/shared/components";

const PageContainer = styled.div`
	padding: 24px;
	background: #eaeefd;
	min-height: 300px;
	border-radius: 16px;
`;

interface CreatorOutreachProps {
	userAssignment?: {
		isAssigned: boolean;
		roles: string[];
	} | null;
}

export const CreatorOutreach: React.FC<CreatorOutreachProps> = ({ userAssignment }) => {
	const [searchParams] = useSearchParams();
	const campaignId = searchParams.get("campaignId");
	const blockId = searchParams.get("blockId");
	const outreachIdParam = searchParams.get("outreachId");
	const [outreachId, setOutreachId] = useState<string | null>(outreachIdParam);
	const navigate = useNavigate();
	const [creators, setCreators] = useState<Creator[]>([]);
	const [rawCreators, setRawCreators] = useState<CreatorWithAssignmentResponse[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [clientVisible, setClientVisible] = useState(true);
	const [viewMode, setViewMode] = useState<"table" | "grid">("grid");
	const [creatorVisibility, setCreatorVisibility] = useState<{ [key: string]: boolean }>({});
	const [isCustomizing, setIsCustomizing] = useState(false);
	const [orderedCreators, setOrderedCreators] = useState<Creator[]>([]);
	const [savedVisibility, setSavedVisibility] = useState<{ [key: string]: boolean }>({});
	const [savedClientVisible, setSavedClientVisible] = useState(true);
	const [finalRateCurrency, setFinalRateCurrency] = useState("USD");
	const [finalRateConvertedCurrency, setFinalRateConvertedCurrency] = useState("AUD");

	// Check if user is client only - use campaign roles from userAssignment
	const STAFF_ROLES = ["Lead", "Support", "Account Manager"];
	const isClientOnly =
		(userAssignment?.roles ?? []).some((r) => r.toLowerCase() === "client") &&
		!(userAssignment?.roles ?? []).some((r) => STAFF_ROLES.includes(r));
	const isAgencyInCampaign = (userAssignment?.roles ?? []).some((r) => STAFF_ROLES.includes(r));
	const canManage = hasRole(ROLES.ADMIN) || isAgencyInCampaign;

	useEffect(() => {
		setCreators(formatCreatorsForDisplay(rawCreators));
	}, [rawCreators]);

	const handleCategoryTierChange = (creatorId: string, field: "categories" | "tier", value: string[] | string) => {
		if (!campaignId) return;

		setRawCreators((prev) =>
			prev.map((item) => {
				if (item.creator.id !== creatorId) return item;
				return {
					...item,
					creator: {
						...item.creator,
						...(field === "categories" && { categories: value as CreatorCategory[] }),
						...(field === "tier" && { tier: value as CreatorTier | "" }),
					},
				};
			}),
		);

		const apiCall =
			field === "categories"
				? creatorAPI.updateCreatorCategories(creatorId, value as string[], campaignId)
				: creatorAPI.updateCreatorTier(creatorId, value as string, campaignId);

		apiCall.catch(() => {
			message.error(`Failed to update ${field === "categories" ? "category" : "tier"}`);
			loadOutreachAndCreators();
		});
	};

	const handleUpdateCreator = (updated: { id: string; contactEmail?: string | null }) => {
		if (updated.contactEmail === undefined) return;
		setRawCreators((prev) =>
			prev.map((item) =>
				item.creator.id === updated.id
					? {
							...item,
							creator: { ...item.creator, contactEmail: updated.contactEmail },
							assignment: { ...item.assignment, contactEmail: updated.contactEmail },
						}
					: item,
			),
		);
	};

	const formatCreatorsForDisplay = useCallback((items: CreatorWithAssignmentResponse[]): Creator[] => {
		return items.map((item) => {
			const creator: Creator = {
				id: item.creator.id,
				name: item.creator.name || "N/A",
				contactEmail: item.creator.contactEmail ?? item.assignment?.contactEmail ?? "",
				assignment: item.assignment,
				assignmentId: item.assignment?.id,
				creatorInterested: item.assignment?.creatorInterested,
				finalRate: item.assignment?.finalRate ?? null,
				finalConvertedRate: item.assignment?.finalConvertedRate ?? null,
			};

			const avatarUrl = extractAvatarUrl(item.socialAccounts);
			if (avatarUrl) {
				creator.img_url = avatarUrl;
			}

			// Basic fields
			creator.category = item.creator.categories || [];
			creator.creatorTier = item.creator.tier || "";
			creator.sampleContent = (item.creator.sampleContents || []).join(", ");
			creator.rationaleForProposing = item.creator.rationaleForProposing || "";
			creator.location = item.creator.location || "";

			// Social accounts per platform
			Object.entries(item.socialAccounts || {}).forEach(([platform, account]) => {
				const key = platform.toLowerCase();
				creator[key] = {
					followers: account.followers,
					avgEngagementRate: account.avgEngagementRate,
					avgViews: account.avgViews,
					localAudiencePercentage: account.localAudiencePercentage,
					femalePercentage: account.femalePercentage,
					malePercentage: account.malePercentage,
					audienceAgeDistribution: account.audienceAgeDistribution,
					platformUsername: account.platformUsername,
				};
			});

			return creator;
		});
	}, []);

	const loadOutreachAndCreators = useCallback(async () => {
		if (!campaignId || !blockId) {
			console.warn("Missing campaignId or blockId");
			setIsLoading(false);
			return;
		}

		setIsLoading(true);
		try {
			// Get outreach ID
			const outreaches = await creatorListAPI.getCreatorOutreaches(campaignId, blockId);
			if (outreaches && outreaches.length > 0) {
				const id = outreaches[0].id;
				setOutreachId(id);

				try {
					const displaySetting = await creatorListAPI.getCreatorDisplaySetting(campaignId, blockId, id);
					const currencies = getFinalRateCurrencies(displaySetting);
					setFinalRateCurrency(currencies.finalRateCurrency);
					setFinalRateConvertedCurrency(currencies.finalRateConvertedCurrency);
				} catch (error) {
					console.warn("Failed to fetch outreach display settings, using defaults:", error);
					setFinalRateCurrency("USD");
					setFinalRateConvertedCurrency("AUD");
				}

				// Get creators from outreach
				const fetchedCreators = await creatorListAPI.getCreatorsFromOutreach(campaignId, blockId, id);
				setRawCreators(fetchedCreators);
				setCreators(formatCreatorsForDisplay(fetchedCreators));
			} else {
				message.warning("No outreach found for this block");
				setCreators([]);
			}
		} catch (error) {
			console.error("Error loading outreach and creators:", error);
			message.error("User is not part of this campaign");
			setCreators([]);
		} finally {
			setIsLoading(false);
		}
	}, [campaignId, blockId, formatCreatorsForDisplay]);

	useEffect(() => {
		loadOutreachAndCreators();
	}, [loadOutreachAndCreators]);

	const handleAddCreator = () => {
		navigate(`/campaign-detail/add-creator?campaignId=${campaignId}&blockId=${blockId}`);
	};

	const shortLinkRef = useRef<string | null>(null);

	useEffect(() => {
		if (!campaignId || !blockId) return;
		const path = window.location.pathname + window.location.search;
		createShortUrl(path)
			.then((res) => {
				shortLinkRef.current = buildShortUrl(res.code, path);
			})
			.catch(console.error);
	}, [campaignId, blockId]);

	const handleGetUrl = async () => {
		const path = window.location.pathname + window.location.search;
		const linkToCopy = shortLinkRef.current || path;

		try {
			await navigator.clipboard.writeText(linkToCopy);
			message.success("Link copied to clipboard!");
		} catch (error) {
			console.error("Clipboard write error:", error);
			message.error("Unable to copy link to clipboard");
		}
	};

	const handleCustomizeOrder = () => {
		if (isCustomizing) {
			setIsCustomizing(false);
			setCreatorVisibility(savedVisibility);
			setClientVisible(savedClientVisible);
			message.info("Customization cancelled");
		} else {
			setOrderedCreators([...creators]);
			setSavedVisibility({ ...creatorVisibility });
			setSavedClientVisible(clientVisible);
			setIsCustomizing(true);
			message.info("Drag rows to reorder");
		}
	};

	const handleSaveOrder = async () => {
		if (isSaving) return;
		if (!campaignId || !blockId) {
			message.error("Missing campaign or block information");
			return;
		}

		// Build updates for creator assignment visibility
		const updates = creators
			.filter((c) => c.assignmentId)
			.map((c) => ({
				assignmentId: c.assignmentId!,
				clientVisible: creatorVisibility[c.id] !== false,
			}));

		setIsSaving(true);
		try {
			await creatorListAPI.updateClientVisibleToCreatorAssignments(campaignId, blockId, updates);
			setCreators(orderedCreators);
			setSavedVisibility({ ...creatorVisibility });
			setSavedClientVisible(clientVisible);
			setIsCustomizing(false);
			message.success("Changes saved successfully!");
		} catch (error) {
			console.error("Error saving visibility changes:", error);
			message.error("Failed to save changes");
		} finally {
			setIsSaving(false);
		}
	};

	const handleClientVisibleToggle = () => {
		if (!isCustomizing) {
			setOrderedCreators([...creators]);
			setSavedVisibility({ ...creatorVisibility });
			setSavedClientVisible(clientVisible);
			setIsCustomizing(true);
		}

		const newClientVisible = !clientVisible;
		setClientVisible(newClientVisible);

		if (newClientVisible) {
			const allVisible: { [key: string]: boolean } = {};
			creators.forEach((creator) => {
				allVisible[creator.id] = true;
			});
			setCreatorVisibility(allVisible);
		} else {
			const allInvisible: { [key: string]: boolean } = {};
			creators.forEach((creator) => {
				allInvisible[creator.id] = false;
			});
			setCreatorVisibility(allInvisible);
		}
	};

	const handleCreatorVisibilityToggle = (creatorId: string) => {
		if (!isCustomizing) {
			setOrderedCreators([...creators]);
			setSavedVisibility({ ...creatorVisibility });
			setSavedClientVisible(clientVisible);
			setIsCustomizing(true);
		}

		const newVisibility = {
			...creatorVisibility,
			[creatorId]: !creatorVisibility[creatorId],
		};
		setCreatorVisibility(newVisibility);

		const allInvisible = creators.every((creator) => !newVisibility[creator.id]);

		if (allInvisible && clientVisible) {
			setClientVisible(false);
		}
	};

	const handleSettingsClick = (creatorId: string) => {
		console.log("Settings clicked for creator:", creatorId);
		message.info("Creator settings functionality coming soon");
	};

	const handleCommentClick = (creatorId: string) => {
		console.log("Comment clicked for creator:", creatorId);
		message.info("Comments functionality coming soon");
	};

	const handleCustomizeOrderButton = () => {
		handleCustomizeOrder();
	};

	const handleCustomizeColumns = () => {
		navigate(
			`/campaign-detail/customize-columns?campaignId=${campaignId}&blockId=${blockId}&displayId=${outreachId}&pageTitle=Creator Outreach`,
		);
	};

	const handleInterestChange = (creatorId: string, interested: CreatorInterestStatus | undefined) => {
		setCreators((prevCreators) =>
			prevCreators.map((creator) =>
				creator.id === creatorId ? { ...creator, creatorInterested: interested } : creator,
			),
		);
		setOrderedCreators((prevCreators) =>
			prevCreators.map((creator) =>
				creator.id === creatorId ? { ...creator, creatorInterested: interested } : creator,
			),
		);
		console.log("Interest changed for creator:", creatorId, "interested:", interested);
	};

	const handleCreatorAddedToOutreach = (creator: Creator) => {
		const contactEmail = creator.contactEmail ?? creator.assignment?.contactEmail ?? "";
		const socialAccounts: any = {};
		const platforms = [
			"instagram",
			"tiktok",
			"youtube",
			"twitter",
			"linkedin",
			"facebook",
			"snapchat",
			"pinterest",
			"twitch",
		];
		platforms.forEach((platform) => {
			if (creator[platform]) {
				socialAccounts[platform.toUpperCase()] = {
					...creator[platform],
					avatarUrl: creator.img_url,
				};
			}
		});

		const minimalRaw: CreatorWithAssignmentResponse = {
			creator: {
				id: creator.id,
				name: creator.name,
				contactEmail,
				categories: (creator.category as any) || [],
				tier: (creator.creatorTier as any) || "",
				location: creator.location || "",
				sampleContents: (creator.sampleContent as string)?.split(", ") || [],
				rationaleForProposing: creator.rationaleForProposing || "",
			},
			assignment: creator.assignment
				? { ...creator.assignment, contactEmail: creator.assignment.contactEmail ?? contactEmail }
				: {
						id: creator.assignmentId || "",
						outreachId: outreachId || undefined,
						creatorInterested: creator.creatorInterested,
						finalRate: creator.finalRate ?? null,
						finalConvertedRate: creator.finalConvertedRate ?? null,
						contactEmail,
					},
			socialAccounts: socialAccounts,
			customInfo: {},
		};

		setRawCreators((prev) => {
			const exists = prev.some((c) => c.creator.id === creator.id);
			if (exists) return prev;
			return [...prev, minimalRaw];
		});
	};

	const handleCreatorRemovedFromOutreach = (creatorId: string) => {
		setRawCreators((prev) => prev.filter((c) => c.creator.id !== creatorId));

		setCreatorVisibility((prev) => {
			const updated = { ...prev };
			delete updated[creatorId];
			return updated;
		});
	};

	const displayCreators = isClientOnly
		? creators.filter((creator) => creatorVisibility[creator.id] !== false)
		: isCustomizing
			? orderedCreators
			: creators;

	useEffect(() => {
		setOrderedCreators([...creators]);

		const initialVisibility: { [key: string]: boolean } = {};
		creators.forEach((creator) => {
			if (creatorVisibility[creator.id] === undefined) {
				initialVisibility[creator.id] = true;
			}
		});

		if (Object.keys(initialVisibility).length > 0) {
			setCreatorVisibility((prev) => ({ ...prev, ...initialVisibility }));
		}
	}, [creators.length]);

	if (isLoading) {
		return (
			<LoadingWrapper isLoading label="Loading creator outreach" borderRadius={12}>
				<PageContainer />
			</LoadingWrapper>
		);
	}

	if (!outreachId) {
		return <div>No outreach found. Please create an outreach first.</div>;
	}

	return (
		<CreatorDisplayLayout
			pageTitle="Creator Outreach"
			addCreatorText="Add/Edit Creators to Outreach"
			displayId={outreachId}
			userAssignment={userAssignment}
			campaignId={campaignId}
			blockId={blockId}
			displayCreators={displayCreators}
			clientVisible={clientVisible}
			viewMode={viewMode}
			creatorVisibility={creatorVisibility}
			isCustomizing={isCustomizing}
			isSaving={isSaving}
			onAddCreator={handleAddCreator}
			onGetUrl={handleGetUrl}
			onCustomizeOrder={handleCustomizeOrder}
			onSaveOrder={handleSaveOrder}
			onClientVisibleToggle={handleClientVisibleToggle}
			onCreatorVisibilityToggle={handleCreatorVisibilityToggle}
			onSettingsClick={handleSettingsClick}
			onCommentClick={handleCommentClick}
			onCustomizeColumns={handleCustomizeColumns}
			onCustomizeOrderButton={handleCustomizeOrderButton}
			onViewModeChange={setViewMode}
			onOrderChange={setOrderedCreators}
			onInterestChange={handleInterestChange}
			outreachId={outreachId}
			canManage={canManage}
			onCategoryTierChange={handleCategoryTierChange}
			onUpdateCreator={handleUpdateCreator}
			onCreatorAddedToOutreach={handleCreatorAddedToOutreach}
			onCreatorRemovedFromOutreach={handleCreatorRemovedFromOutreach}
			finalRateCurrency={finalRateCurrency}
			finalRateConvertedCurrency={finalRateConvertedCurrency}
		/>
	);
};
